import type { MessageDescriptor } from '@lingui/core';
import { useMemo, useState, useCallback } from "react";
import { BaseEditableTable } from "@/components/table/BaseEditableTable";
import type { FilterConfig } from "@/components/table/types";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { exportToCSV, getAbsenceCSVColumns } from "@/utils/csvExport";
import type { RowSelectionState } from "@tanstack/react-table";
import { useLingui } from "@lingui/react/macro";
import { useLingui as useLinguiRuntime } from "@lingui/react";
import { getAbsenceColumns } from "@/utils/columnDefinitions";
import { getAbsenceTypeLabel } from "@/components/absences/AbsenceTypeBadge";
import type {
  EmployeeAbsence,
  AbsenceTypeValue,
  CertificateModeValue,
} from "@/types/employeeAbsences";
import { AbsenceType } from "@/types/employeeAbsences";

interface AbsenceTableProps {
  data: EmployeeAbsence[];
  onEditAbsence: (absence: EmployeeAbsence) => void;
  enableSelection?: boolean;
  // Report row actions (#1767). All optional: without a handler the table
  // renders no actions column (D27), so other call sites stay unchanged.
  onAcknowledge?: (absence: EmployeeAbsence) => void;
  onCancelReport?: (absence: EmployeeAbsence) => void;
  onEauRetrieved?: (absence: EmployeeAbsence) => void;
  onEditDates?: (absence: EmployeeAbsence) => void;
  onViewCertificates?: (absence: EmployeeAbsence) => void;
  /** The organisation's certificate mode; "eAU retrieved" is offered only in EAU mode. */
  certificateMode?: CertificateModeValue;
  /** Admin or `employees` holder: cancel report, eAU retrieved, edit dates. */
  canWrite?: boolean;
  /** Admin or `employees` holder: the certificate viewer. */
  canReadCertificates?: boolean;
}

export function AbsenceTable({
  data,
  onEditAbsence,
  enableSelection = true,
  onAcknowledge,
  onCancelReport,
  onEauRetrieved,
  onEditDates,
  onViewCertificates,
  certificateMode,
  canWrite = false,
  canReadCertificates = false,
}: AbsenceTableProps) {
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const { t } = useLingui();
  const { i18n } = useLinguiRuntime();

  // Build absence type options for filter using the existing label function
  const absenceTypeOptions = useMemo(
    () =>
      Object.values(AbsenceType).map((type) => ({
        value: type,
        label: getAbsenceTypeLabel(type as AbsenceTypeValue, i18n),
      })),
    [i18n]
  );

  // Define the columns for the absence table
  const columns = useMemo(
    () =>
      getAbsenceColumns(
        // Identical ternary branches on purpose (GroupTable.tsx precedent): `i18n._`'s
        // overloads cannot be resolved for a union argument; collapsing reintroduces TS2769.
        (template: string | MessageDescriptor): string =>
          typeof template === 'string' ? i18n._(template) : i18n._(template),
        {
        onAvatarClick: onEditAbsence,
        absenceTypeOptions,
        reportActions: {
          onAcknowledge,
          onCancelReport,
          onEauRetrieved,
          onEditDates,
          onViewCertificates,
          certificateMode,
          canWrite,
          canReadCertificates,
        },
      }),
    // `t` (Lingui's per-locale macro binding) is retained so the memo re-runs
    // on language switch and headers re-translate via i18n._; ESLint flags it as
    // unused because the body calls i18n._, but it is the locale-change signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      i18n,
      onEditAbsence,
      absenceTypeOptions,
      onAcknowledge,
      onCancelReport,
      onEauRetrieved,
      onEditDates,
      onViewCertificates,
      certificateMode,
      canWrite,
      canReadCertificates,
    ]
  );

  // Define filter configuration - employee and type are select filters, dates are date filters
  const filterConfig: FilterConfig<EmployeeAbsence> = useMemo(
    () => ({
      absence_type: "select",
      from_date: "date",
      to_date: "date",
    }),
    []
  );

  // Handle CSV export
  const handleExportCSV = useCallback(
    (selectedRowIds: string[]) => {
      // Get selected absences or all if none selected
      const absencesToExport =
        selectedRowIds.length > 0
          ? data.filter((absence) => selectedRowIds.includes(absence.id || ""))
          : data;

      if (absencesToExport.length === 0) {
        toast.error(t`table.absences.actions.noAbsencesToExport`);
        return;
      }

      const timestamp = new Date().toISOString().split("T")[0];
      const filename =
        selectedRowIds.length > 0
          ? `absences-selected-${timestamp}`
          : `absences-all-${timestamp}`;

      try {
        exportToCSV(absencesToExport, getAbsenceCSVColumns(), filename);
        toast.success(
          t`table.absences.actions.exportSuccess`.replace(
            "{count}",
            absencesToExport.length.toString()
          )
        );
      } catch (error) {
        toast.error(t`table.absences.actions.exportError`);
        console.error("CSV export error:", error);
      }
    },
    [data, t]
  );

  // Render selection actions (export button)
  const renderSelectionActions = useCallback(
    (selectedRowIds: string[], clearSelection: () => void) => (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleExportCSV(selectedRowIds)}
          className="flex items-center space-x-2"
        >
          <Download className="h-4 w-4" />
          <span>
            {t`table.absences.actions.exportSelected`.replace(
              "{count}",
              selectedRowIds.length.toString()
            )}
          </span>
        </Button>
        <Button variant="ghost" size="sm" onClick={clearSelection}>
          {t`table.absences.actions.clearSelection`}
        </Button>
      </>
    ),
    [handleExportCSV, t]
  );

  // No cell update handler needed since the table is read-only
  const noOpCellUpdate = useCallback(async () => {
    // No-op - table is read-only
  }, []);

  return (
    <BaseEditableTable<EmployeeAbsence>
      data={data}
      columns={columns}
      onCellUpdate={noOpCellUpdate}
      defaultSortField="from_date"
      defaultSortOrder="desc"
      defaultPageSize={10}
      enableSelection={enableSelection}
      selectedRows={selectedRows}
      onSelectionChange={setSelectedRows}
      renderSelectionActions={renderSelectionActions}
      enableSearch={true}
      searchPlaceholder={t`table.absences.search.placeholder`}
      enableFilters={true}
      filterConfig={filterConfig}
      enableColumnVisibility={true}
      enableColumnOrdering={true}
      tableId="absence-table"
    />
  );
}
